import { useNavigate } from 'react-router-dom'
import { CalendarDays, MapPin } from 'lucide-react'
import type { TShow } from '@schemas'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatDateRangeDateOnly } from '@/lib/date'
import { STATUS_LABELS } from '@/lib/show-status'

function statusVariant(status?: TShow['status']) {
  switch (status) {
    case 'accepting_entries':
    case 'judging':
      return 'default'
    case 'finalized':
      return 'secondary'
    default:
      return 'outline'
  }
}

function ShowStatusBadge({ show }: { show: TShow }) {
  return <Badge variant={statusVariant(show.status)}>{STATUS_LABELS[show.status] ?? 'Draft'}</Badge>
}

function ShowDates({ show }: { show: TShow }) {
  return (
    <span className="inline-flex items-center gap-2">
      <CalendarDays aria-hidden="true" className="size-4 shrink-0" />
      {formatDateRangeDateOnly(show.startDate, show.endDate)}
    </span>
  )
}

function ShowLocation({ location }: { location: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <MapPin aria-hidden="true" className="size-4 shrink-0" />
      <span className="break-words">{location}</span>
    </span>
  )
}

export interface ShowReadinessSummary {
  classes: {
    ready: boolean
    total: number
  }
  staff: {
    ready: boolean
    counts: {
      judge: number
      lead_secretary: number
      secretary: number
      steward: number
    }
  }
}

export function SelectShowNotice({
  title = 'Select a show to continue',
  description = 'Choose a show from the dashboard or shows list before managing entries, classes, or reports.',
  actionLabel = 'Choose a show',
  secondaryActionLabel = 'Go to dashboard',
}: {
  title?: string
  description?: string
  actionLabel?: string
  secondaryActionLabel?: string
}) {
  const navigate = useNavigate()

  return (
    <Card className="border-dashed border-primary/40 bg-primary/5">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        <Button onClick={() => navigate('/shows')}>{actionLabel}</Button>
        <Button variant="outline" onClick={() => navigate('/')}>
          {secondaryActionLabel}
        </Button>
      </CardContent>
    </Card>
  )
}

/** The current show's name, phase, dates and location for a page header, without the card or its actions. */
export function CompactShowSummary({ show, className }: { show: TShow; className?: string }) {
  return (
    <div className={cn('space-y-2 text-sm', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{show.name}</span>
        <ShowStatusBadge show={show} />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
        <ShowDates show={show} />
        {show.location && <ShowLocation location={show.location} />}
      </div>
    </div>
  )
}

export function CurrentShowSummary({
  show,
  className,
  onEdit,
  readiness,
  showActions = true,
}: {
  show: TShow
  className?: string
  onEdit?: () => void
  readiness?: ShowReadinessSummary
  showActions?: boolean
}) {
  const navigate = useNavigate()

  return (
    <Card className={cn('border border-muted bg-muted/30', className)}>
      <CardHeader className="space-y-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base md:text-lg">
          Working on <span className="text-primary">{show.name}</span>
          <ShowStatusBadge show={show} />
        </CardTitle>
        <CardDescription>
          <ShowDates show={show} />
        </CardDescription>
        {show.location && (
          <p className="text-sm text-muted-foreground">
            <ShowLocation location={show.location} />
          </p>
        )}
        {showActions ? (
          <div className="flex flex-wrap gap-2">
            {onEdit ? (
              <Button variant="secondary" size="sm" onClick={onEdit}>
                Edit details
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              Dashboard
            </Button>
          </div>
        ) : null}
      </CardHeader>
      {readiness ? (
        <CardContent className="border-t border-muted/60 pt-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">Classes configured</p>
                <p className="text-xs text-muted-foreground">
                  {readiness.classes.ready
                    ? `${readiness.classes.total} class${readiness.classes.total === 1 ? '' : 'es'} available`
                    : 'Add at least one class to proceed.'}
                </p>
              </div>
              <Badge variant={readiness.classes.ready ? 'default' : 'outline'}>
                {readiness.classes.ready ? 'Ready' : 'Missing'}
              </Badge>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">Staff coverage</p>
                <p className="text-xs text-muted-foreground">
                  Judges: {readiness.staff.counts.judge} • Lead secretaries:{' '}
                  {readiness.staff.counts.lead_secretary} • Secretaries:{' '}
                  {readiness.staff.counts.secretary} • Stewards: {readiness.staff.counts.steward}
                </p>
              </div>
              <Badge variant={readiness.staff.ready ? 'default' : 'outline'}>
                {readiness.staff.ready ? 'Ready' : 'Missing'}
              </Badge>
            </div>
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}
